import { useTranslation } from "react-i18next";
import { RxCaretDown, RxArrowDown } from "react-icons/rx";
import ExpandableRow from "./ExpandableRow";
import { useState, useMemo, useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";
import { getName } from "js/formatting";
import RowPagination from "components/General/RowPagination";
import { useRowPagination } from "hooks/useRowPagination";

export const ServiceRequest = ({ data, setOpenChatWindow }) => {
  const { t } = useTranslation();
  const { agentPolicies } = useContext(AgentData);

  const [selectedStatuses, setSelectedStatuses] = useState(["All"]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState(["All"]);
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "desc",
  });
  const [dropdownState, setDropdownState] = useState({
    isOpenStatus: false,
    isOpenServiceType: false,
  });

  const uniqueServiceTypes = [
    "All",
    ...Array.from(new Set(data.map((item) => item.desc))).filter(Boolean),
  ];

  const uniqueStatuses = [
    "All",
    ...Array.from(new Set(data.map((item) => item.statusText))).filter(Boolean),
  ];

  const getOwnerName = (contractID) => {
    const policy = agentPolicies?.find((p) => p.contractID === contractID);
    const party = policy?.parties?.find(
      (x) => x.person?.type?.toLowerCase() === "primary owner"
    );
    return party ? getName(party.person) : "";
  };

  const toggleDropdown = (dropdown) => {
    setDropdownState((prev) => ({
      isOpenStatus: dropdown === "isOpenStatus" ? !prev.isOpenStatus : false,
      isOpenServiceType:
        dropdown === "isOpenServiceType" ? !prev.isOpenServiceType : false,
    }));
  };

  const handleServiceTypeChange = (option) => {
    setSelectedServiceTypes((prev) => {
      let next;
      if (option === "All") next = ["All"];
      else if (prev.includes(option))
        next = prev.filter((item) => item !== option);
      else next = [...prev.filter((item) => item !== "All"), option];
      if (next.length === 0) next = ["All"];
      setDropdownState((d) => ({ ...d, isOpenServiceType: false }));
      return next;
    });
  };

  const handleStatusChange = (option) => {
    setSelectedStatuses((prev) => {
      let next;
      if (option === "All") next = ["All"];
      else if (prev.includes(option))
        next = prev.filter((item) => item !== option);
      else next = [...prev.filter((item) => item !== "All"), option];
      if (next.length === 0) next = ["All"];
      setDropdownState((d) => ({ ...d, isOpenStatus: false }));
      return next;
    });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setDropdownState({ isOpenStatus: false, isOpenServiceType: false });
  };

  const filteredData = useMemo(() => {
    let filtered = data;
    if (!selectedServiceTypes.includes("All")) {
      filtered = filtered.filter((item) =>
        selectedServiceTypes.includes(item.desc)
      );
    }
    if (!selectedStatuses.includes("All")) {
      filtered = filtered.filter((item) =>
        selectedStatuses.includes(item.statusText)
      );
    }
    return filtered;
  }, [data, selectedServiceTypes, selectedStatuses]);

  const sortedData = useMemo(() => {
    if (sortConfig.key === "contractID" || sortConfig.key === "receivedDate") {
      const sorted = [...filteredData].sort((a, b) => {
        if (sortConfig.key === "contractID") {
          if (sortConfig.direction === "asc") {
            return a.oldPolicy.localeCompare(b.oldPolicy);
          } else {
            return b.oldPolicy.localeCompare(a.oldPolicy);
          }
        }
        if (sortConfig.key === "receivedDate") {
          const dateA = new Date(a.receivedDate);
          const dateB = new Date(b.receivedDate);
          if (sortConfig.direction === "asc") {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        }
        return 0;
      });
      return sorted;
    }
    const initiated = filteredData.filter(
      (item) => item.statusText === "Initiated"
    );
    const pending = filteredData.filter(
      (item) => item.statusText === "Pending"
    );
    const closed = filteredData.filter((item) => item.statusText === "Closed");

    const sortByDateDesc = (a, b) =>
      new Date(b.receivedDate) - new Date(a.receivedDate);

    initiated.sort(sortByDateDesc);
    pending.sort(sortByDateDesc);
    closed.sort(sortByDateDesc);

    return [...initiated, ...pending, ...closed];
  }, [filteredData, sortConfig]);

  const {
    rowsForDisplay: serviceRows,
    rowPaginationProps: servicePaginationProps,
  } = useRowPagination(sortedData, [5, 10, 25, "all"]);

  const Dropdown = ({
    label,
    isOpen,
    toggleDropdown,
    options,
    selectedOptions,
    handleSelectionChange,
  }) => (
    <div className="text-xs font-bold text-theme-text-secondary md:col-span-2 cursor-pointer">
      <div className="relative inline-block">
        <div
          className="flex items-center justify-between w-full"
          onClick={toggleDropdown}
        >
          {label}
          <span>
            <RxCaretDown
              className="ui-btn-hyperlink"
              style={{ transform: `${isOpen ? "rotate(180deg)" : ""}` }}
              size={23}
            />
          </span>
        </div>
        {isOpen && (
          <div className="absolute left-0 z-10 w-48 mt-1 origin-top-right bg-white border border-gray-300 rounded-md shadow-lg">
            {options.map((option) => (
              <label
                className="ui-checkbox mt-1 flex items-center px-4 py-1"
                key={option}
              >
                <input
                  type="checkbox"
                  value={option}
                  checked={selectedOptions.includes(option)}
                  onChange={() => handleSelectionChange(option)}
                />
                <div className="ui-checkmark" style={{ borderRadius: 0 }}>
                  <svg
                    className="ui-icon w-2 h-2 text-white"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-checkmark"></use>
                  </svg>
                </div>
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="ui-card-bar relative mt-4">
        <div className="px-3 py-2">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4 xl:gap-4">
            <Dropdown
              label={t("CustomerServiceRequest-service-status")}
              isOpen={dropdownState.isOpenStatus}
              toggleDropdown={() => toggleDropdown("isOpenStatus")}
              options={uniqueStatuses}
              selectedOptions={selectedStatuses}
              handleSelectionChange={handleStatusChange}
            />
            <Dropdown
              label={t("CustomerServiceRequest-service-type")}
              isOpen={dropdownState.isOpenServiceType}
              toggleDropdown={() => toggleDropdown("isOpenServiceType")}
              options={uniqueServiceTypes}
              selectedOptions={selectedServiceTypes}
              handleSelectionChange={handleServiceTypeChange}
            />

            <div className="text-xs font-bold text-theme-text-secondary md:col-span-1 md:text-center cursor-pointer">
              <div className="relative inline-block">
                <div
                  className="flex items-center justify-between w-full mr-1"
                  onClick={() => handleSort("contractID")}
                >
                  {t("CustomerServiceRequest-policy")}
                  <span>
                    <RxArrowDown
                      style={{
                        transform:
                          sortConfig.key === "contractID" &&
                          sortConfig.direction === "asc"
                            ? "rotate(180deg)"
                            : "",
                      }}
                      size={18}
                    />
                  </span>
                </div>
              </div>
            </div>
            <div className="text-xs font-bold  text-theme-text-secondary md:col-span-1 md:text-center">
              {t("CustomerServiceRequest-service-id")}
            </div>
            <div className="text-xs font-bold  text-theme-text-secondary md:col-span-1 cursor-pointer">
              <div className="relative inline-block">
                <div
                  className="flex items-center justify-between w-full mr-1"
                  onClick={() => handleSort("receivedDate")}
                >
                  {t("CustomerServiceRequest-received-date")}
                  <span>
                    <RxArrowDown
                      style={{
                        transform:
                          sortConfig.key === "receivedDate" &&
                          sortConfig.direction === "asc"
                            ? "rotate(180deg)"
                            : "",
                      }}
                      size={18}
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {serviceRows.map((item) => {
        return (
          <ExpandableRow
            key={item.taskID}
            data={item}
            setOpenChatWindow={setOpenChatWindow}
            ownerName={getOwnerName(item.contractID)}
          />
        );
      })}
      {serviceRows.length > 0 && <RowPagination {...servicePaginationProps} />}
    </>
  );
};
